import { spaceApi } from "@circle-react/api/spaceApi";
import type { IndexResponseData } from "@circle-react/api/spaceApi";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { createQueryKeyFactory } from "@circle-react/lib/queries/createQueryKeyFactory";
import { queryClient } from "@circle-react/providers/QueryClient/reactQueryConfig";
import type { PaginationHelper, Space } from "@circle-react/types";
import type { SearchSpace } from "@circle-react/types/Search/Space";
import type { SpaceResource } from "@circle-react/types/Space";
import { infiniteQueryOptions, queryOptions } from "react-query";

export const spaceKey = createQueryKeyFactory("space");

interface SettingsListParams {
  filter: Record<string, unknown>;
  page: number;
  perPage: number;
  sort: string;
}

interface CourseDirectoryParams {
  filter: string;
  topics: string | (string | null)[] | null | undefined;
}

interface SpacesResponse {
  records: Space[];
}

interface SpaceListParams {
  includeSidebar?: boolean;
}

export const spaceQueries = {
  list: ({ includeSidebar = false }: SpaceListParams = {}) =>
    queryOptions({
      queryKey: spaceKey.key(
        "list",
        includeSidebar ? "with-sidebar" : "without-sidebar",
      ),
      queryFn: () =>
        spaceApi.list({
          params: { include_sidebar: includeSidebar },
        }) as Promise<IndexResponseData>,
    }),
  detail: (id: string | number) =>
    queryOptions({
      queryKey: spaceKey.key("detail", id),
      queryFn: () =>
        spaceApi.show<SpaceResource>({
          id: Number(id),
          forReactQuery: true,
        }) as Promise<SpaceResource>,
    }),
  show: (spaceId: number) => spaceQueries.detail(spaceId),
  resourceDetail: (id?: number) =>
    queryOptions({
      ...spaceQueries.detail(id ?? "disabled"),
      enabled: Boolean(id),
    }),
  workflowDetail: (id: string) =>
    queryOptions({
      queryKey: spaceKey.key("detail", id),
      queryFn: () => reactQueryGet<Space>(internalApi.spaces.show({ id })),
    }),
  activeMembersCount: (id: string | number) =>
    queryOptions({
      queryKey: spaceKey.key("active-members-count", id),
      queryFn: () =>
        reactQueryGet<{ members_count: number }>(
          internalApi.spaces.activeMembersCount({ id }),
        ),
    }),
  workflowsList: (perPage = 100) =>
    queryOptions({
      queryKey: spaceKey.key("workflows", "list", perPage),
      queryFn: () =>
        spaceApi.list({
          params: { per_page: perPage },
        }),
    }),
  settingsList: ({ page, filter, perPage, sort }: SettingsListParams) =>
    queryOptions({
      queryKey: spaceKey.key("settings", "list", page, JSON.stringify(filter)),
      queryFn: () =>
        reactQueryGet<PaginationHelper<SearchSpace>>(
          internalApi.search.spaces.index({
            params: {
              ...filter,
              page,
              per_page: perPage,
              sort,
            },
          }),
        ),
    }),
  settingsCount: (status = "") =>
    queryOptions({
      queryKey: spaceKey.key("settings", "count", status),
      queryFn: () =>
        reactQueryGet<PaginationHelper<SearchSpace>>(
          internalApi.search.spaces.index({
            params: {
              page: 1,
              per_page: 1,
              ...(status ? { status } : {}),
            },
          }),
        ),
    }),
  spaceGroupDetailsList: (spaceGroupId?: number) =>
    queryOptions({
      queryKey: spaceKey.key("space-group-details", spaceGroupId ?? "disabled"),
      queryFn: () =>
        reactQueryGet<{ records: Space[] }>(
          internalApi.spaces.index({
            params: {
              space_group_id: spaceGroupId,
              include_details: true,
            },
          }),
        ),
      enabled: !!spaceGroupId,
    }),
  courseSpaces: () =>
    queryOptions({
      queryKey: spaceKey.key("course-spaces"),
      queryFn: (): Promise<Space[]> =>
        reactQueryGet<SpacesResponse>(
          internalApi.spaces.index({
            params: { post_type: "course", per_page: 100 },
          }),
        ).then(response => response.records),
      staleTime: 5 * 60 * 1000,
    }),
  courseDirectory: ({ filter, topics }: CourseDirectoryParams) =>
    infiniteQueryOptions({
      initialPageParam: 1,
      queryKey: spaceKey.key(
        "course-directory",
        filter,
        JSON.stringify(topics ?? []),
      ),
      queryFn: ({ pageParam = 1 }: { pageParam?: number }) =>
        reactQueryGet<PaginationHelper<SearchSpace>>(
          internalApi.search.spaces.index({
            params: {
              page: pageParam,
              space_type: "course",
              sort: "directory",
              per_page: 30,
              only_joined: filter === "joined",
              topics: topics || [],
            },
          }),
        ),
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    }),
};

export const spaceInvalidations = {
  all: () => queryClient.invalidateQueries({ queryKey: spaceKey.root() }),
  courseDirectory: () =>
    queryClient.invalidateQueries({
      queryKey: spaceKey.key("course-directory"),
    }),
};
